'use client'

import React, { useState } from 'react'
import classNames from 'classnames'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { Label } from '@/ui/atoms/Label'
import { Icon } from '@/ui/atoms/Icon'
import { useTranslations } from 'next-intl'
import { Media, ButtonField, Vendor } from '@/payload-types'
import { Profile } from '@/ui/atoms/Profile'
import { Avatar } from '@/ui/atoms/Avatar'
import { Dialog, DialogTrigger } from '@/ui/components/Dialog'
import { EventModal } from './EventModal'
import { EventHeader } from './EventHeader'
import type { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical'
import { TypedLocale } from 'payload'
import { trackEvent } from '@/lib/analytics/posthog'

type AuthorData = {
  image?: Media | string | null
  name?: string
}


export type EventCardProps = {
  title: string
  startDate?: string
  endDate?: string
  location?: string
  category?: {
    title: string
    colorScheme: string
  }
  labels?: string[] // TODO: change to ILabel[]
  className?: string
  // Recommended section props
  recommendedBy?: AuthorData[]
  isRecommended?: boolean
  recommendedWhy?: string | null
  longRecommendation?: string | null
  image?: Media | string | null
  // Modal additional fields
  description?: SerializedEditorState | null
  organizer?: string | null
  organizerIsLinkable?: boolean | null
  organizerLinkType?: 'internal' | 'external' | null
  organizerVendor?: Vendor | string | null
  organizerLink?: string | null
  language?: string | null
  link?: ButtonField | null
  showCTAButton?: boolean | null
  secondLink?: ButtonField | null
  showSecondCTAButton?: boolean | null
  locale?: TypedLocale
  /**
   * Directory mode (Events page, 2026-09): when set, the card is a link to the event's own
   * page instead of a modal trigger; the recommendation quote is always visible and the
   * organizer website is one click away. Legacy callers (carousel, calendar) leave it unset
   * and keep the modal behaviour untouched.
   */
  detailHref?: string | null
  /**
   * Directory mode WITHOUT per-event pages (2026-09-16, per-event pages parked pending
   * Connor's view): same card design, but the card and its "Details" open the modal.
   */
  directory?: boolean
  websiteUrl?: string | null
  speakingLine?: string | null
  /** "32nd edition" / "First edition" / "30+ years" (derived, see lib/events editionOf) */
  edition?: string | null
  /** Organiser link for the "by …" line (internal vendor path or external URL). */
  organizerHref?: string | null
  excerpt?: string | null
  featured?: boolean
  analyticsSurface?: string
}

export const EventCard: React.FC<EventCardProps> = ({
  className: classNameFromProps,
  title,
  startDate,
  endDate,
  location,
  category,
  labels,
  recommendedBy,
  isRecommended,
  recommendedWhy,
  longRecommendation,
  image,
  description,
  organizer,
  organizerIsLinkable,
  organizerLinkType,
  organizerVendor,
  organizerLink,
  language,
  link,
  showCTAButton,
  secondLink,
  showSecondCTAButton,
  locale,
  detailHref,
  directory: directoryProp = false,
  websiteUrl,
  speakingLine,
  edition,
  organizerHref,
  excerpt,
  featured = false,
  analyticsSurface,
}) => {
  const t = useTranslations('public')
  const [open, setOpen] = useState(false)
  const router = useRouter()

  const directory = directoryProp || Boolean(detailHref)
  const attending = Boolean(isRecommended && recommendedBy && recommendedBy.length > 0)

  const modal = (
    <EventModal
      title={title}
      startDate={startDate}
      endDate={endDate}
      location={location}
      category={category}
      labels={labels}
      image={image}
      description={description}
      organizer={organizer}
      organizerIsLinkable={organizerIsLinkable}
      organizerLinkType={organizerLinkType}
      organizerVendor={organizerVendor}
      organizerLink={organizerLink}
      language={language}
      link={link}
      showCTAButton={showCTAButton}
      secondLink={secondLink}
      showSecondCTAButton={showSecondCTAButton}
      isRecommended={isRecommended}
      recommendedBy={recommendedBy}
      recommendedWhy={recommendedWhy}
      longRecommendation={longRecommendation}
      locale={locale}
    />
  )

  const labelsRow = ((labels && labels.length > 0) || category) && (
    <div className="flex flex-wrap gap-2">
      {category && <Label title={category.title} colorScheme={category.colorScheme} size="small" />}
      {labels &&
        labels.length > 0 &&
        (() => {
          // Show 2 tags for exactly 4 tags, otherwise show 3
          const visibleCount = labels.length === 4 ? 2 : 3
          const shouldTruncate = labels.length > 3

          return (
            <>
              {labels.slice(0, visibleCount).map((label) => (
                <Label title={label} variant="neutral" key={label} />
              ))}
              {shouldTruncate && (
                <>
                  {labels.slice(visibleCount).map((label) => (
                    <Label
                      title={label}
                      variant="neutral"
                      key={label}
                      className="hidden group-hover:flex"
                    />
                  ))}
                  <Label
                    title={`+${labels.length - visibleCount} more Tags`}
                    variant="subtle-blue"
                    className="group-hover:hidden"
                  />
                </>
              )}
            </>
          )
        })()}
    </div>
  )

  // ---------------------------------------------------------------------------------
  // Directory mode: whole card links to /events/<slug>; website link is one click away.
  // ---------------------------------------------------------------------------------
  if (directory) {
    const track = (name: string) =>
      trackEvent(name, { event: title, href: detailHref ?? null, surface: analyticsSurface ?? 'list' })

    const go = () => {
      track('event_card_click')
      if (detailHref) router.push(detailHref)
      else setOpen(true)
    }

    const card = (
      <article
        role="link"
        tabIndex={0}
        aria-label={title}
        onClick={go}
        onKeyDown={(e) => {
          if (e.key === 'Enter') go()
        }}
        className={classNames(
          'group relative flex flex-col rounded-md transition-colors overflow-hidden w-full min-w-0 border-border-subtle border-[1px] border-solid bg-bg-base hover:bg-bg-subtle hover:border-gray-50 cursor-pointer focus:outline-border-focus focus:outline-[1px]',
          featured ? 'min-h-56' : 'min-h-40 md:min-h-48',
          classNameFromProps,
        )}
      >
        <div className="relative p-5 flex-grow flex flex-col gap-4">
          <EventHeader
            title={title}
            startDate={startDate}
            endDate={endDate}
            location={location}
            organizer={organizer}
            organizerHref={organizerHref}
            showEventDate={true}
            titleAs="h3"
            aligned
            eyebrow={
              category ? (
                <Label title={category.title} colorScheme={category.colorScheme} size="small" />
              ) : undefined
            }
          />

          {featured && excerpt && (
            <p className="text-body-S-regular text-text-subtle line-clamp-3">{excerpt}</p>
          )}

          {/* Quotes, speaking sentences and topic tags deliberately NOT on cards (Veronika
              2026-09-15): the band carries "Speaker", the event page carries the rest. */}
          <div className="mt-auto flex flex-col gap-4">
            <div className="flex items-center justify-between gap-3 pt-3 border-t border-border-subtle">
              <div className="flex items-center gap-3 min-w-0">
                {edition && <Label title={edition} variant="neutral" size="small" className="shrink-0" />}
                {detailHref ? (
                  <Link
                    href={detailHref}
                    onClick={(e) => {
                      e.stopPropagation()
                      track('event_card_click')
                    }}
                    className="inline-flex items-center gap-1 text-label-XS-semibold text-text-base group-hover:underline"
                  >
                    {t('event-details')}
                    <Icon name="arrow-right-line" className="!size-4" />
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      go()
                    }}
                    className="inline-flex items-center gap-1 text-label-XS-semibold text-text-base group-hover:underline"
                  >
                    {t('event-details')}
                    <Icon name="arrow-right-line" className="!size-4" />
                  </button>
                )}
              </div>
              {websiteUrl && (
                <a
                  href={websiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    e.stopPropagation()
                    trackEvent('event_website_click', {
                      event: title,
                      url: websiteUrl,
                      surface: analyticsSurface ?? 'list',
                    })
                  }}
                  className="inline-flex items-center gap-1 whitespace-nowrap shrink-0 text-label-XS-regular text-text-subtle hover:text-text-brand hover:underline"
                >
                  {t('event-website')}
                  <Icon name="external-link-line" className="!size-3.5" />
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Uniform bottom band on EVERY card (Veronika 2026-09-15): blue "Electrification Academy
            will be there" with the experts for events we attend, otherwise "Recommended Event"
            (everything listed is recommended by EA, that's why it's on the list). */}
        <div
          className={classNames(
            'relative px-5 min-h-[52px] flex items-center justify-between gap-3',
            attending ? 'bg-bg-brand-blue' : 'bg-bg-subtle border-t border-border-subtle',
          )}
        >
          {attending ? (
            <>
              <div className="flex items-center gap-2 shrink-0">
                <div className="flex items-center -space-x-2" aria-label={recommendedBy!.map((a) => a.name).join(', ')}>
                  {recommendedBy!.map((author) =>
                    author.image && typeof author.image === 'object' ? (
                      <div key={author.name} className="rounded-full ring-2 ring-white" title={author.name}>
                        <Avatar variant="circle" userImage={author.image} size="mini" />
                      </div>
                    ) : null,
                  )}
                </div>
                {speakingLine && (
                  <span
                    className="inline-flex items-center gap-1 rounded-full bg-blue-20 text-text-inverted px-2 py-0.5 text-label-XS-semibold"
                    title={speakingLine}
                  >
                    <Icon name="presentation-fill" className="!size-3" />
                    Speaker
                  </span>
                )}
              </div>
              <span className="text-label-XS-semibold text-blue-20 text-right">Electrification Academy will be there</span>
            </>
          ) : (
            <>
              <Icon name="ri-calendar-event-fill" className="!size-4 text-text-faded" />
              <span className="text-label-XS-regular text-text-subtle">{t('recommended-event')}</span>
            </>
          )}
        </div>
      </article>
    )

    if (detailHref) return card
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        {card}
        {modal}
      </Dialog>
    )
  }

  // ---------------------------------------------------------------------------------
  // Legacy mode (carousel, calendar): card opens the modal.
  // ---------------------------------------------------------------------------------
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <div
          className={classNames(
            'group min-h-40 md:min-h-48 flex flex-col rounded-md transition-colors overflow-hidden w-full border-border-subtle border-[1px] border-solid bg-bg-base hover:bg-bg-subtle hover:border-gray-50 cursor-pointer',
            classNameFromProps,
          )}
        >
          <div className="p-5 flex-grow flex flex-col justify-between gap-6">
            <div className="flex justify-between items-start">
              <div className="space-y-2 flex-grow">
                <EventHeader
                  title={title}
                  startDate={startDate}
                  endDate={endDate}
                  location={location}
                  showEventDate={true}
                  titleAs="h3"
                />
              </div>
            </div>

            {labelsRow}
          </div>

          {isRecommended && recommendedBy && (
            <div className="px-5 py-4 bg-bg-brand-blue flex flex-col gap-2">
              {recommendedWhy && (
                <div className="max-h-0 overflow-hidden opacity-0 group-hover:max-h-20 group-hover:opacity-100 transition-all duration-200 text-label-XS-regular text-text-faded">
                  {`"${recommendedWhy}"`}
                </div>
              )}
              <div className="flex justify-between items-center">
                <div className="xl:flex items-center gap-2">
                  {recommendedBy.map((author) => (
                    <Profile
                      key={author.name}
                      name={author.name}
                      image={typeof author.image === 'object' ? author.image : undefined}
                      size="mini"
                    />
                  ))}
                </div>
                <div className="text-label-XS-regular text-blue-20">{t('recommended-event')}</div>
              </div>
            </div>
          )}
        </div>
      </DialogTrigger>
      {modal}
    </Dialog>
  )
}
